import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { useLibraryStore } from "../store/library-store";

const CONTROLS_IDLE_MS = 2500;

type WebkitVideoElement = HTMLVideoElement & { webkitEnterFullscreen?: () => void };

interface UseVideoPlayerOptions {
  videoRef: RefObject<HTMLVideoElement | null>;
  rootRef: RefObject<HTMLElement | null>;
  canHover: boolean;
}

const isTypingTarget = (target: EventTarget | null) => {
  const element = target as HTMLElement | null;
  if (!element) return false;
  return (
    ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(element.tagName) ||
    element.isContentEditable
  );
};

/**
 * Detay oynatıcısının durum ve aksiyonları.
 * Ses, sessiz ve hız tercihleri library store'da kalıcı tutulur.
 */
export const useVideoPlayer = ({ videoRef, rootRef, canHover }: UseVideoPlayerOptions) => {
  const volume = useLibraryStore((state) => state.playerVolume);
  const isMuted = useLibraryStore((state) => state.playerMuted);
  const rate = useLibraryStore((state) => state.playerRate);
  const setVolumeStore = useLibraryStore((state) => state.setPlayerVolume);
  const setMuted = useLibraryStore((state) => state.setPlayerMuted);
  const setRateStore = useLibraryStore((state) => state.setPlayerRate);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedEnd, setBufferedEnd] = useState(0);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const [pinCount, setPinCount] = useState(0);
  const idleTimerRef = useRef<number | null>(null);

  // Arka plan ambiyansı kareleri bu elemandan okur
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const { setActiveVideoEl } = useLibraryStore.getState();
    setActiveVideoEl(video);
    return () => {
      if (useLibraryStore.getState().activeVideoEl === video) setActiveVideoEl(null);
    };
  }, [videoRef]);

  // Tercihleri video elemanına uygula
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = volume;
    video.muted = isMuted;
  }, [isMuted, videoRef, volume]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.playbackRate = rate;
  }, [rate, videoRef]);

  // Video olayları
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateBuffered = () => {
      const { buffered } = video;
      for (let i = buffered.length - 1; i >= 0; i--) {
        if (buffered.start(i) <= video.currentTime + 0.1) {
          setBufferedEnd(buffered.end(i));
          return;
        }
      }
      setBufferedEnd(0);
    };
    const handleMetadata = () => {
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
      if (video.videoWidth && video.videoHeight) {
        setAspectRatio(video.videoWidth / video.videoHeight);
      }
      video.playbackRate = useLibraryStore.getState().playerRate;
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleTime = () => setCurrentTime(video.currentTime);

    video.addEventListener("loadedmetadata", handleMetadata);
    video.addEventListener("durationchange", handleMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("timeupdate", handleTime);
    video.addEventListener("seeked", handleTime);
    video.addEventListener("progress", updateBuffered);
    if (video.readyState >= 1) handleMetadata();

    return () => {
      video.removeEventListener("loadedmetadata", handleMetadata);
      video.removeEventListener("durationchange", handleMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("timeupdate", handleTime);
      video.removeEventListener("seeked", handleTime);
      video.removeEventListener("progress", updateBuffered);
    };
  }, [videoRef]);

  // Oynarken ilerleme çubuğu akıcı ilerlesin (timeupdate ~4 Hz)
  useEffect(() => {
    if (!isPlaying) return;
    let frame = 0;
    const tick = () => {
      const video = videoRef.current;
      if (video) setCurrentTime(video.currentTime);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isPlaying, videoRef]);

  // Otomatik oynatma — sesli oynatma engellenirse sessize düş
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.play().catch((error: DOMException) => {
      if (error?.name !== "NotAllowedError" || video.muted) return;
      video.muted = true;
      setMuted(true);
      video.play().catch(() => undefined);
    });
  }, [setMuted, videoRef]);

  useEffect(() => {
    const handleChange = () =>
      setIsFullscreen(!!rootRef.current && document.fullscreenElement === rootRef.current);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, [rootRef]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => undefined);
    else video.pause();
  }, [videoRef]);

  const seek = useCallback(
    (time: number) => {
      const video = videoRef.current;
      if (!video || !Number.isFinite(video.duration)) return;
      const clamped = Math.min(Math.max(time, 0), video.duration);
      video.currentTime = clamped;
      setCurrentTime(clamped);
    },
    [videoRef]
  );

  const setVolume = useCallback(
    (next: number) => {
      setVolumeStore(next);
      setMuted(next === 0);
    },
    [setMuted, setVolumeStore]
  );

  const toggleMute = useCallback(() => {
    const nextMuted = !useLibraryStore.getState().playerMuted;
    setMuted(nextMuted);
    // Ses 0'dayken açılırsa duyulabilir bir seviyeye getir
    if (!nextMuted && useLibraryStore.getState().playerVolume === 0) setVolumeStore(0.6);
  }, [setMuted, setVolumeStore]);

  const setRate = useCallback((next: number) => setRateStore(next), [setRateStore]);

  const toggleFullscreen = useCallback(() => {
    const root = rootRef.current;
    const video = videoRef.current as WebkitVideoElement | null;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
      return;
    }
    if (root?.requestFullscreen) {
      root.requestFullscreen().catch(() => undefined);
    } else {
      // iOS Safari yalnızca video elemanını tam ekrana alabiliyor
      video?.webkitEnterFullscreen?.();
    }
  }, [rootRef, videoRef]);

  // Kontrollerin otomatik gizlenmesi
  const clearIdleTimer = () => {
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = null;
  };

  const revealControls = useCallback(() => {
    setIsIdle(false);
    clearIdleTimer();
    idleTimerRef.current = window.setTimeout(() => setIsIdle(true), CONTROLS_IDLE_MS);
  }, []);

  const hideControls = useCallback(() => {
    clearIdleTimer();
    setIsIdle(true);
  }, []);

  useEffect(() => {
    revealControls();
    return clearIdleTimer;
  }, [revealControls]);

  /** Menü açıkken veya sürüklerken kontroller gizlenmesin */
  const pinControls = useCallback((pinned: boolean) => {
    setPinCount((count) => Math.max(0, count + (pinned ? 1 : -1)));
  }, []);

  // Kontroller normalde hep görünür; yalnızca tam ekranda hareketsizlikte gizlenir
  const isControlsVisible = !isFullscreen || !isPlaying || !isIdle || pinCount > 0;

  const handleVideoClick = useCallback(() => {
    // Tam ekranda dokunuş kontrolleri açar/kapatır, oynatma butondan yapılır
    if (!canHover && isFullscreen) {
      if (isControlsVisible) hideControls();
      else revealControls();
      return;
    }
    togglePlay();
    revealControls();
  }, [canHover, hideControls, isControlsVisible, isFullscreen, revealControls, togglePlay]);

  // Klavye: Space/K oynat, M ses, F tam ekran. Oklar detay navigasyonuna ait.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      if (document.querySelector('[role="menu"]')) return;
      const key = event.key.toLowerCase();
      if (key === " " || key === "k") {
        event.preventDefault();
        togglePlay();
      } else if (key === "m") {
        toggleMute();
      } else if (key === "f") {
        toggleFullscreen();
      } else {
        return;
      }
      revealControls();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [revealControls, toggleFullscreen, toggleMute, togglePlay]);

  return {
    isPlaying,
    currentTime,
    duration,
    bufferedEnd,
    aspectRatio,
    isFullscreen,
    isControlsVisible,
    volume,
    isMuted,
    rate,
    togglePlay,
    seek,
    setVolume,
    toggleMute,
    setRate,
    toggleFullscreen,
    revealControls,
    hideControls,
    pinControls,
    handleVideoClick,
  };
};

export type VideoPlayerApi = ReturnType<typeof useVideoPlayer>;

export const formatPlayerTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
};
